/**
 * JobDetail — the per-job workspace in the flow-through chain (/job/:jobNum).
 *
 * One page that shows a job the way Jobber does — visits, logged hours,
 * supplier expenses, customer invoices, notes + photos — without leaving
 * ElecBid, with the FlowStrip on top to jump anywhere else in the chain.
 *
 * Data: /api/flow action:jobDetail (jb_jobs row + jobber_visits +
 * jobber_timesheets + pending_invoices + live Jobber invoices);
 * /api/takeoff action:jobMedia for the workflow-linked notes (request → quote
 * → job, rolled downstream the way Jobber does, with authors); and
 * /api/job-media for archived photos (signed URLs, note_author + taken_at).
 *
 * Notes & Photos renders as one collapsible Jobber-style timeline: grouped by
 * day, tech chips per day, photos nested under the note they were posted with.
 * Archive photos are canonical; live Jobber attachments fill the sweep lag so
 * photos taken today still show. Every section header is a one-click toggle.
 *
 * Access mirrors the Jobs tab: the route is registered crew-tier with the
 * view_jobs / view_crew_metrics keys, and the API 403s without them.
 */
import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch } from "../lib/apiFetch";
import FlowStrip from "./FlowStrip";
import { Lightbox } from "./MediaGallery";

const C = {
  bg: "#0d0f12", surface: "#161a20", card: "#1c2028", border: "#2a3140",
  lime: "#a5ce3a", text: "#e8eaf0", muted: "#8892a4",
  green: "#22c55e", amber: "#eab308", red: "#ef4444", blue: "#60a5fa",
};

const money = (n) => (n == null || isNaN(+n) ? "—" : "$" + (+n).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtTime = (d) => (d ? new Date(d).toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" }) : "");

const INVOICE_STATUS_COLORS = {
  paid: C.green, awaiting_payment: C.amber, past_due: C.red, bad_debt: C.red, draft: C.muted, sent: C.blue,
};

function Section({ title, count, children, innerRef, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div ref={innerRef} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 16, marginBottom: 14 }}>
      <button onClick={() => setOpen((o) => !o)}
        style={{ display: "flex", alignItems: "baseline", gap: 8, width: "100%", background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", fontFamily: "inherit", marginBottom: open ? 10 : 0 }}>
        <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 19, letterSpacing: ".05em", color: C.lime }}>{title}</span>
        {count != null && <span style={{ color: C.muted, fontSize: 15, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: ".05em" }}>{count}</span>}
        <span style={{ marginLeft: "auto", color: C.muted, fontSize: 13 }}>{open ? "▾" : "▸"}</span>
      </button>
      {open && children}
    </div>
  );
}

const TECH_CHIP = { background: "rgba(165,206,58,.12)", border: "1px solid rgba(165,206,58,.35)", color: C.lime, borderRadius: 999, padding: "1px 8px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" };
const SRC_CHIP = { background: "rgba(96,165,250,.12)", border: "1px solid rgba(96,165,250,.4)", color: C.blue, borderRadius: 5, padding: "0 6px", fontSize: 10, fontWeight: 700, textTransform: "capitalize", whiteSpace: "nowrap" };
const firstName = (s) => String(s || "").trim().split(/\s+/)[0] || null;

function Thumb({ p, label, onOpen, size = 76 }) {
  const tile = { width: size, height: size, objectFit: "cover", borderRadius: 8, border: `1px solid ${C.border}`, display: "block", background: C.card };
  return (
    <div onClick={onOpen} title={[p.noteAuthor, fmtDate(p.takenAt), p.fileName].filter(Boolean).join(" · ")} style={{ position: "relative", cursor: "pointer" }}>
      {p.isVideo ? (
        <div style={{ ...tile, display: "flex", alignItems: "center", justifyContent: "center", color: C.text, fontSize: 22 }}>▶</div>
      ) : (
        <img src={p.thumbUrl || p.url} alt={p.fileName || "photo"} loading="lazy" style={tile} />
      )}
      {label && (
        <span style={{ position: "absolute", left: 4, bottom: 4, background: "rgba(6,8,10,.75)", color: C.lime, fontSize: 9.5, fontWeight: 700, borderRadius: 5, padding: "1px 5px", maxWidth: size - 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
      )}
    </div>
  );
}

function StatusChip({ value, color }) {
  if (!value) return <span style={{ color: C.muted }}>—</span>;
  const c = color || C.muted;
  return (
    <span style={{ background: c + "22", color: c, border: `1px solid ${c}55`, borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
      {String(value).replace(/_/g, " ")}
    </span>
  );
}

export default function JobDetail() {
  const { jobNum } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [media, setMedia] = useState(null);     // jobMedia: { notes: [], media: [] } — workflow-linked notes + live attachments
  const [archive, setArchive] = useState(null); // /api/job-media rows — archived photos with noteAuthor/takenAt
  const [lightbox, setLightbox] = useState(null);
  const invoicesRef = useRef(null);

  useEffect(() => {
    let dead = false;
    setLoading(true); setError(null); setData(null); setMedia(null); setArchive(null); setLightbox(null);
    const NOTES_DOWN = { notes: [], media: [], reason: "Jobber notes unavailable right now." };
    // Archived photos are keyed by job number — no need to wait for the flow fetch.
    apiFetch("/api/job-media", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "list", job: jobNum }),
    }).then((r) => r.json()).then((j) => { if (!dead) setArchive(j.ok === false ? [] : j.media || []); })
      .catch(() => { if (!dead) setArchive([]); });
    (async () => {
      try {
        const r = await apiFetch("/api/flow", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "jobDetail", jobNum }),
        });
        const j = await r.json();
        if (dead) return;
        if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
        setData(j);
        // Workflow-linked notes (request → quote → job) ride the takeoff jobMedia action.
        if (j.job?.jobber_id) {
          apiFetch("/api/takeoff", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action: "jobMedia", jobberJobId: j.job.jobber_id, limit: 50 }),
          }).then((mr) => mr.json())
            .then((mj) => { if (!dead) setMedia(mj.ok === false ? NOTES_DOWN : mj); })
            .catch(() => { if (!dead) setMedia(NOTES_DOWN); });
        } else {
          setMedia({ notes: [], media: [], reason: "This job isn't linked to Jobber." });
        }
      } catch (e) { if (!dead) setError(e.message); }
      finally { if (!dead) setLoading(false); }
    })();
    return () => { dead = true; };
  }, [jobNum]);

  // ?focus=invoices deep-link (the Invoices chip on the FlowStrip)
  useEffect(() => {
    if (!loading && data && params.get("focus") === "invoices" && invoicesRef.current) {
      invoicesRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [loading, data, params]);

  const hoursByTech = useMemo(() => {
    const map = {};
    for (const ts of (data?.timesheets || [])) {
      const name = ts.user_name || "Unknown";
      map[name] = (map[name] || 0) + (Number(ts.duration_hours) || 0);
    }
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [data]);
  const totalHours = hoursByTech.reduce((s, [, h]) => s + h, 0);

  // Merge workflow notes + photos into a Jobber-style timeline: days newest
  // first, entries chronological within the day, photos nested under the note
  // they were posted with (matched on the shared note text both sources carry).
  const feed = useMemo(() => {
    const norm = (s) => String(s || "").replace(/\s+/g, " ").trim().slice(0, 60).toLowerCase();
    const dayKey = (iso) => (iso ? new Date(iso).toLocaleDateString("en-CA") : "undated");
    const byTime = (get) => (a, b) => String(get(a) || "").localeCompare(String(get(b) || ""));

    const arch = archive || [];
    const photosArch = arch.filter((m) => m.isImage || m.isVideo);
    const docsArch = arch.filter((m) => !m.isImage && !m.isVideo);

    // Live Jobber attachments cover the archive sweep lag (files added today);
    // archive rows are canonical, so drop live copies the archive already has.
    // Both sides key on the FILE's timestamp day (jobMedia stamps file-level
    // createdAt, matching the archive's taken_at semantics).
    const seenFile = new Set(arch.map((m) => `${String(m.fileName || "").toLowerCase()}|${dayKey(m.takenAt)}`));
    const fresh = (list) => (list || []).filter((f) => !seenFile.has(`${String(f.fileName || "").toLowerCase()}|${dayKey(f.createdAt)}`));
    const liveShape = (f, id, isVideo) => ({ id, url: f.url, thumbUrl: f.thumb || null, fileName: f.fileName, isImage: !isVideo, isVideo, takenAt: f.createdAt || null, noteAuthor: f.author || null, noteMessage: f.noteMessage || null });
    const live = fresh(media?.media).map((f, i) => liveShape(f, `live-${i}`, false));
    const liveVids = [], liveDocs = [];
    fresh(media?.attachments).forEach((f, i) => {
      if (/^video\//i.test(f.contentType || "")) liveVids.push(liveShape(f, `livev-${i}`, true));
      else liveDocs.push({ id: `lived-${i}`, url: f.url, fileName: f.fileName, takenAt: f.createdAt || null });
    });

    // Bridged timesheet notes are API-authored — the real tech, workday and
    // message live in the body ("⏱ Timesheet note — Tech (YYYY-MM-DD): …").
    // Group them under the WORKDAY, not the day the nightly bridge pushed the
    // mirror; superseded mirrors are pure noise here. Note↔photo matching is
    // scoped to the day: short note texts ("Site visit") recur across days,
    // so text alone would nest photos under the wrong note.
    const TS_RE = /^⏱\s*Timesheet note — (.+?) \((.+?)\):\s*([\s\S]*)$/;
    const TS_NO_DAY_RE = /^⏱\s*Timesheet note — ([^(:]+?):\s*([\s\S]*)$/;
    const notes = (media?.notes || [])
      .filter((n) => !/^⏱\s*Timesheet note — \(superseded/.test(n.message || ""))
      .map((n) => {
        const ts = TS_RE.exec(n.message || "");
        const ts2 = ts ? null : TS_NO_DAY_RE.exec(n.message || "");
        const workDay = ts && /^\d{4}-\d{2}-\d{2}$/.test(ts[2]) ? ts[2] : null;
        const key = norm(n.message);
        return {
          ...n, matchKey: key ? `${dayKey(n.createdAt)}|${key}` : null, isTimesheet: !!(ts || ts2), workDay,
          author: ts ? ts[1] : ts2 ? ts2[1].trim() : n.author || null,
          displayMessage: ts ? ts[3] : ts2 ? ts2[2] : n.message, photos: [],
        };
      });
    const byMatch = new Map();
    for (const n of notes) if (n.matchKey && !byMatch.has(n.matchKey)) byMatch.set(n.matchKey, n);

    const days = new Map();
    const dayOf = (k) => { if (!days.has(k)) days.set(k, { key: k, notes: [], photos: [], docs: [] }); return days.get(k); };
    for (const p of [...photosArch, ...live, ...liveVids]) {
      const pKey = norm(p.noteMessage);
      const host = pKey ? byMatch.get(`${dayKey(p.takenAt)}|${pKey}`) : null;
      if (host) host.photos.push(p);
      else dayOf(dayKey(p.takenAt)).photos.push(p);
    }
    for (const n of notes) dayOf(n.workDay || dayKey(n.createdAt)).notes.push(n);
    for (const d0 of [...docsArch, ...liveDocs]) dayOf(dayKey(d0.takenAt)).docs.push(d0);

    const daysArr = [...days.values()].sort((a, b) =>
      ((a.key === "undated") - (b.key === "undated")) || String(b.key).localeCompare(String(a.key)));
    const all = [];
    for (const d0 of daysArr) {
      d0.notes.sort(byTime((n) => n.createdAt));
      d0.photos.sort(byTime((p) => p.takenAt));
      d0.label = d0.key === "undated"
        ? "Older — no date on file"
        : new Date(`${d0.key}T12:00:00`).toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
      d0.techs = [...new Set([
        ...d0.notes.map((n) => n.author),
        ...d0.notes.flatMap((n) => n.photos.map((p) => p.noteAuthor)),
        ...d0.photos.map((p) => p.noteAuthor),
      ].filter(Boolean))];
      all.push(...d0.photos);
      for (const n of d0.notes) {
        n.photos.sort(byTime((p) => p.takenAt));
        all.push(...n.photos);
      }
    }
    return { days: daysArr, all };
  }, [media, archive]);
  // The lightbox is keyed by photo id, not index — the feed can reorder when
  // the slower of the two photo sources lands while the viewer is open.
  const lbIndex = lightbox != null ? feed.all.findIndex((p) => p.id === lightbox) : -1;
  const npLoading = media === null || archive === null;
  const npCount = npLoading ? null : `${(media.notes || []).length} notes · ${feed.all.length} photos`;

  const job = data?.job;
  const pageStyle = { maxWidth: 1000, margin: "0 auto", padding: "20px 16px 40px", color: C.text, fontFamily: "'Roboto', sans-serif" };

  if (loading) return <div style={pageStyle}><div style={{ textAlign: "center", padding: 40, color: C.muted }}>Loading job…</div></div>;
  if (error || !job) {
    return (
      <div style={pageStyle}>
        <button onClick={() => navigate(-1)} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, color: C.muted, padding: "6px 14px", cursor: "pointer" }}>‹ Back</button>
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, marginTop: 16, color: C.red }}>
          {error || "Job not found"}
        </div>
      </div>
    );
  }

  const payColor = INVOICE_STATUS_COLORS[String(job.derived_status || job.invoice_status || "").toLowerCase()] ||
    (job.amount_outstanding > 0 ? C.amber : job.amount_invoiced ? C.green : C.muted);

  return (
    <div style={pageStyle}>
      <FlowStrip anchor={{ job: jobNum }} style={{ marginBottom: 14 }} />

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{ minWidth: 220 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 30, letterSpacing: ".04em", color: C.lime, lineHeight: 1.1 }}>
            Job #{job.job_num}
          </div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{job.client_name || "—"}</div>
          {job.title && <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>{job.title}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <StatusChip value={job.status} color={C.blue} />
            <StatusChip value={job.derived_status || job.invoice_status || "no invoice"} color={payColor} />
            {job.service_type && <StatusChip value={job.service_type} />}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {data.takeoffSheet && (
            <button onClick={() => navigate(`/takeoff?sheet=${data.takeoffSheet.id}`)}
              style={{ background: "rgba(165,206,58,.12)", border: "1px solid rgba(165,206,58,.4)", borderRadius: 8, color: C.lime, fontWeight: 700, fontSize: 12, padding: "8px 14px", cursor: "pointer" }}>
              Material Takeoff
            </button>
          )}
          {job.webUri && (
            <button onClick={() => window.open(job.webUri, "_blank", "noopener")}
              style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontWeight: 600, fontSize: 12, padding: "8px 14px", cursor: "pointer" }}>
              Open in Jobber ↗
            </button>
          )}
        </div>
      </div>

      {/* ── Money summary ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 14 }}>
        {[
          ["Revenue", money(job.total_revenue), C.text],
          ["Invoiced", money(job.amount_invoiced), C.text],
          ["Outstanding", money(job.amount_outstanding), job.amount_outstanding > 0 ? C.amber : C.text],
          ["Profit", job.profit != null ? `${money(job.profit)}${job.profit_pct != null ? ` (${Number(job.profit_pct).toFixed(0)}%)` : ""}` : "—",
            job.profit == null ? C.muted : job.profit >= 0 ? C.green : C.red],
        ].map(([label, value, color]) => (
          <div key={label} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "10px 14px" }}>
            <div style={{ fontSize: 10, letterSpacing: ".08em", textTransform: "uppercase", color: C.muted, fontWeight: 700 }}>{label}</div>
            <div style={{ fontSize: 17, fontWeight: 700, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* ── Visits ── */}
      <Section title="Visits" count={(data.visits || []).length || null}>
        {(data.visits || []).length === 0 && <div style={{ color: C.muted, fontSize: 13 }}>No visits synced for this job.</div>}
        {(data.visits || []).map((v) => (
          <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: "1px solid #1e2328", fontSize: 13, flexWrap: "wrap" }}>
            <span style={{ color: v.is_complete ? C.green : C.muted, width: 16, textAlign: "center" }}>{v.is_complete ? "✓" : "○"}</span>
            <span style={{ minWidth: 150, fontWeight: 600 }}>
              {fmtDate(v.start_at)}
              <span style={{ color: C.muted, fontWeight: 400 }}> {fmtTime(v.start_at)}{v.end_at ? `–${fmtTime(v.end_at)}` : ""}</span>
            </span>
            <span style={{ flex: 1, minWidth: 140, color: C.muted }}>{v.title || "—"}</span>
            <span style={{ color: C.muted, fontSize: 12 }}>
              {(v.assigned_users || []).map((u) => u.name).filter(Boolean).join(", ")}
            </span>
          </div>
        ))}
        {hoursByTech.length > 0 && (
          <div style={{ marginTop: 10, fontSize: 12, color: C.muted }}>
            <span style={{ fontWeight: 700, color: C.text }}>Logged hours: {totalHours.toFixed(1)}</span>
            {" — "}{hoursByTech.map(([name, h]) => `${name} ${h.toFixed(1)}h`).join(" · ")}
          </div>
        )}
      </Section>

      {/* ── Expenses ── */}
      <Section title="Expenses" count={(data.expenses || []).length || null}>
        {(data.expenses || []).length === 0 && <div style={{ color: C.muted, fontSize: 13 }}>No supplier invoices linked to this job yet.</div>}
        {(data.expenses || []).length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ color: C.muted, textAlign: "left" }}>
                  {["Supplier", "Invoice #", "Type", "Date", "Total", "Status"].map((h) => (
                    <th key={h} style={{ padding: "6px 10px", fontWeight: 600, borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.expenses.map((e) => (
                  <tr key={e.id} style={{ borderBottom: "1px solid #1e2328" }}>
                    <td style={{ padding: "6px 10px", fontWeight: 600 }}>{e.supplier || "—"}</td>
                    <td style={{ padding: "6px 10px", color: C.muted }}>{e.invoice_number || "—"}</td>
                    <td style={{ padding: "6px 10px", color: C.muted }}>{e.expense_type || "—"}</td>
                    <td style={{ padding: "6px 10px", color: C.muted, whiteSpace: "nowrap" }}>{fmtDate(e.invoice_date || e.created_at)}</td>
                    <td style={{ padding: "6px 10px", fontWeight: 600, whiteSpace: "nowrap" }}>{money(e.total)}</td>
                    <td style={{ padding: "6px 10px" }}><StatusChip value={e.status} color={e.status === "pushed" ? C.green : C.amber} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* ── Invoices ── */}
      <Section title="Invoices" count={(data.invoices || []).length || null} innerRef={invoicesRef}>
        {data.invoices == null && (
          <div style={{ color: C.muted, fontSize: 13 }}>
            Live invoice list unavailable right now — rollup from the last sync:{" "}
            invoiced <span style={{ color: C.text, fontWeight: 600 }}>{money(job.amount_invoiced)}</span>, outstanding{" "}
            <span style={{ color: job.amount_outstanding > 0 ? C.amber : C.text, fontWeight: 600 }}>{money(job.amount_outstanding)}</span>
            {job.invoice_nums ? ` (invoice #${job.invoice_nums})` : ""}.
          </div>
        )}
        {Array.isArray(data.invoices) && data.invoices.length === 0 && (
          <div style={{ color: C.muted, fontSize: 13 }}>No invoices on this job yet.</div>
        )}
        {(data.invoices || []).map((inv) => (
          <div key={inv.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: "1px solid #1e2328", fontSize: 13, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, minWidth: 70 }}>#{inv.number}</span>
            <StatusChip value={inv.status} color={INVOICE_STATUS_COLORS[String(inv.status || "").toLowerCase()]} />
            <span style={{ color: C.muted, minWidth: 100 }}>{fmtDate(inv.issuedDate)}</span>
            <span style={{ fontWeight: 600, minWidth: 90 }}>{money(inv.total)}</span>
            <span style={{ color: inv.balance > 0 ? C.amber : C.muted, fontSize: 12 }}>
              {inv.balance > 0 ? `${money(inv.balance)} owing` : "paid in full"}
            </span>
            {inv.webUri && (
              <a href={inv.webUri} target="_blank" rel="noreferrer" style={{ color: "#7dd3fc", fontSize: 12, textDecoration: "none", marginLeft: "auto" }}>
                open in Jobber ↗
              </a>
            )}
          </div>
        ))}
      </Section>

      {/* ── Notes & Photos — one collapsible Jobber-style timeline. Notes are the
             workflow union (request → quote → job, rolled downstream like Jobber);
             photos come from OUR archive (signed URLs that always work) plus live
             Jobber attachments to cover the sweep lag, nested under their note. ── */}
      <Section title="Notes & Photos" count={npCount} defaultOpen={false}>
        {npLoading && <div style={{ color: C.muted, fontSize: 13 }}>Loading notes & photos…</div>}
        {/* a Jobber outage must stay visible even when archive photos render */}
        {!npLoading && media.reason && feed.days.length > 0 && (
          <div style={{ color: C.amber, fontSize: 12, marginBottom: 8 }}>{media.reason}</div>
        )}
        {!npLoading && feed.days.length === 0 && (
          <div style={{ color: C.muted, fontSize: 13 }}>{media.reason || "No notes or photos on this job yet."}</div>
        )}
        {feed.days.map((day) => (
          <div key={day.key} style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "8px 0 5px", borderBottom: "1px solid #1e2328" }}>
              <span style={{ fontWeight: 700, fontSize: 13 }}>{day.label}</span>
              {day.techs.map((t) => <span key={t} style={TECH_CHIP}>{t}</span>)}
            </div>
            {day.photos.length > 0 && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", padding: "8px 0 2px" }}>
                {day.photos.map((p) => (
                  <Thumb key={p.id} p={p} label={firstName(p.noteAuthor)} onOpen={() => setLightbox(p.id)} />
                ))}
              </div>
            )}
            {day.notes.map((n, i) => (
              <div key={i} style={{ borderLeft: `2px solid ${C.border}`, padding: "6px 0 6px 10px", margin: "8px 0" }}>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap", alignItems: "baseline", fontSize: 11.5, color: C.muted, marginBottom: 3 }}>
                  <span style={{ color: C.text, fontWeight: 700 }}>{n.author || "ElecBid"}</span>
                  {/* a bridged mirror's createdAt is push time, not the workday — no time */}
                  {n.createdAt && !n.isTimesheet && <span>{fmtTime(n.createdAt)}</span>}
                  {n.source && n.source !== "job" && <span style={SRC_CHIP}>{n.source}</span>}
                  {n.isTimesheet && <span style={SRC_CHIP}>timesheet</span>}
                  {n.files > n.photos.length && <span>📎 {n.files - n.photos.length}</span>}
                </div>
                <div style={{ fontSize: 13, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{n.displayMessage}</div>
                {n.photos.length > 0 && (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
                    {n.photos.map((p) => (
                      <Thumb key={p.id} p={p} onOpen={() => setLightbox(p.id)} />
                    ))}
                  </div>
                )}
              </div>
            ))}
            {day.docs.length > 0 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                {day.docs.map((m) => (
                  <a key={m.id} href={m.url} target="_blank" rel="noreferrer"
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: "5px 10px", fontSize: 12, color: C.text, textDecoration: "none", maxWidth: 240 }}>
                    <span>📄</span>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.fileName || "document"}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
        {lbIndex >= 0 && (
          <Lightbox items={feed.all} index={lbIndex}
            onClose={() => setLightbox(null)}
            onStep={(d0) => setLightbox(feed.all[(lbIndex + d0 + feed.all.length) % feed.all.length].id)} />
        )}
      </Section>
    </div>
  );
}
